/**
 * What to tell Sentry and the learner when the lesson media player fails.
 * Pure helpers so the component stays thin and the shapes are testable.
 *
 * Until 2026-09 the player's onError logged, reported a contextless
 * `Error("Player error occurred")`, and left the line that would show an
 * error state commented out — 43 people in 30 days saw a player that never
 * started and no explanation (WEB-SPACE-1QX), and the report carried nothing
 * that identified the dead source.
 */

export type MediaErrorDetail = {
  /** HTMLMediaElement `MediaError.code` (1 aborted, 2 network, 3 decode, 4 src not supported). */
  code: number | null;
  message: string;
};

const MEDIA_ERROR_NAMES: Record<number, string> = {
  1: "aborted",
  2: "network",
  3: "decode",
  4: "src-not-supported",
};

/** Normalise a React media `onError` event (or anything the player hands us). */
export function describeMediaError(event: unknown): MediaErrorDetail {
  const target =
    (event as { currentTarget?: unknown } | undefined)?.currentTarget ??
    (event as { target?: unknown } | undefined)?.target;
  const mediaError = (target as { error?: { code?: unknown; message?: unknown } } | undefined)
    ?.error;
  const code = typeof mediaError?.code === "number" ? mediaError.code : null;
  const rawMessage =
    typeof mediaError?.message === "string" && mediaError.message
      ? mediaError.message
      : typeof (event as { message?: unknown } | undefined)?.message === "string"
        ? ((event as { message: string }).message)
        : "";
  const name = code !== null ? (MEDIA_ERROR_NAMES[code] ?? `code-${code}`) : "unknown";
  return { code, message: rawMessage || name };
}

export type MediaSourceDescription = {
  /** `upload` = a file Mindstone stores (signed `fileUrl`); `link` = an external `url`. */
  kind: "upload" | "link" | "none";
  host: string;
  /** Origin + path only — never the query string, which can carry a signature. */
  location: string;
};

export function describeMediaSource(input: {
  fileUrl?: string | null;
  url?: string | null;
}): MediaSourceDescription {
  const src = input.fileUrl || input.url || "";
  if (!src) return { kind: "none", host: "", location: "" };
  const kind = input.fileUrl ? "upload" : "link";
  try {
    const parsed = new URL(src);
    return { kind, host: parsed.host, location: `${parsed.origin}${parsed.pathname}` };
  } catch {
    // A relative or malformed source: keep the path, drop anything after `?`.
    return { kind, host: "", location: src.split("?")[0] ?? "" };
  }
}
